import { crmListObjectType } from "app/pages/sales-intelligence/domain/crm-integration/types/object-type";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";

import ExcludeByCrmFilterContainer from "../../components/filters/ExcludeByCrmFilter/ExcludeByCrmFilterContainer";
import ExcludeByCrmFilterResultContainer from "../../components/filters/ExcludeByCrmFilter/ExcludeByCrmFilterResultContainer";
import { excludeByCrmCapability } from "../../types/leadsCapability";
import { leadsFilterApiCategory } from "../../types/leadsFilterApiCategory";
import { leadsFilterCategoryMap } from "../../types/leadsFilterCategory";
import { defineDescriptionFilter } from "../defineSourcedFilter";

import ExcludeByCrmFilter from "./ExcludeByCrmFilter";

const trackingNameByProvider: Record<CrmIntegrationProvider, string> = {
    hubspot: "Exclude websites from your HubSpot companies",
    salesforce: "Exclude websites from your Salesforce accounts",
    dynamics: "Exclude websites from your Dynamics accounts",
};

// One per provider. Visibility is the `exclude-by-crm:<provider>` capability, which `createLeadsGrants`
// resolves from the old `isExcludeByCrmFilterHidden` gate (surface exclude availability × CRM-integration
// entitlement × the custom-filters supersession); the account fields come from the description.
const makeExcludeByCrmDefinition = <P extends CrmIntegrationProvider>(provider: P) =>
    defineDescriptionFilter({
        key: provider,
        apiCategory: leadsFilterApiCategory[provider],
        category: leadsFilterCategoryMap.exclude,
        trackingName: trackingNameByProvider[provider],
        titleKey: { key: `si.lead_gen_filters.exclude_by_${provider}.name` },
        requiresCapabilities: [excludeByCrmCapability(provider)],

        render: ({ description }) => {
            const fields =
                description.integrationsLists.find(
                    (c) => c.integrationName === provider && c.objectType === crmListObjectType.accounts,
                )?.fields ?? [];

            return {
                tooltipText: { key: `si.lead_gen_filters.exclude_by_${provider}.tooltip` },
                filter: new ExcludeByCrmFilter({
                    options: {
                        fields,
                        integrationName: provider,
                    },
                }),
                filterView: ExcludeByCrmFilterContainer,
                filterResultView: ExcludeByCrmFilterResultContainer,
            };
        },
    });

export const salesforceExcludeDefinition = makeExcludeByCrmDefinition("salesforce");
export const hubspotExcludeDefinition = makeExcludeByCrmDefinition("hubspot");
export const dynamicsExcludeDefinition = makeExcludeByCrmDefinition("dynamics");
